'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DateSelectionCalendarProps {
  onDatesChange: (checkIn: Date | null, checkOut: Date | null) => void;
}

export default function DateSelectionCalendar({ onDatesChange }: DateSelectionCalendarProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      onDatesChange(checkInDate, checkOutDate);
    }
  }, [checkInDate, checkOutDate, isHydrated, onDatesChange]);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const isDateUnavailable = (date: Date) => {
    // Mock unavailable dates (e.g., already booked)
    const unavailableDates = [5, 12, 19, 26]; // Days of month that are booked
    return unavailableDates.includes(date.getDate());
  };

  const isDateInPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateInRange = (date: Date) => {
    if (!checkInDate || !checkOutDate) {
      if (checkInDate && hoveredDate) {
        return date > checkInDate && date < hoveredDate;
      }
      return false;
    }
    return date > checkInDate && date < checkOutDate;
  };

  const handleDateClick = (date: Date) => {
    if (isDateInPast(date) || isDateUnavailable(date)) return;

    if (!checkInDate) {
      setCheckInDate(date);
      setCheckOutDate(null);
    } else if (!checkOutDate) {
      if (date > checkInDate) {
        setCheckOutDate(date);
      } else {
        setCheckInDate(date);
        setCheckOutDate(null);
      }
    } else {
      setCheckInDate(date);
      setCheckOutDate(null);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    const today = new Date();
    const prevMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (prevMonthDate >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCurrentMonth(prevMonthDate);
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:112:16" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="112" data-component-end-line="112" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-square%22%7D" key={`empty-${i}`} className="aspect-square"></div>);
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isUnavailable = isDateUnavailable(date);
      const isPast = isDateInPast(date);
      const isCheckIn = checkInDate?.toDateString() === date.toDateString();
      const isCheckOut = checkOutDate?.toDateString() === date.toDateString();
      const isInRange = isDateInRange(date);
      const isDisabled = isPast || isUnavailable;

      days.push(
        <button data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:126:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="126" data-component-end-line="148" data-component-file="DateSelectionCalendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
          key={day}
          onClick={() => handleDateClick(date)}
          onMouseEnter={() => !isDisabled && setHoveredDate(date)}
          onMouseLeave={() => setHoveredDate(null)}
          onTouchStart={() => !isDisabled && setHoveredDate(date)}
          onTouchEnd={() => setHoveredDate(null)}
          disabled={isDisabled}
          type="button"
          aria-label={`Select ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`}
          className={`aspect-square flex items-center justify-center text-sm font-medium rounded-lg transition-all touch-manipulation select-none ${
            isCheckIn || isCheckOut
              ? 'bg-primary text-primary-foreground'
              : isInRange
              ? 'bg-primary/10 text-primary'
              : isDisabled
              ? 'text-muted-foreground cursor-not-allowed opacity-40'
              : 'text-foreground hover:bg-muted active:bg-muted'
          }`}
          style={{ WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none' }}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:157:6" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="157" data-component-end-line="166" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-xl%20border%20border-border%20p-6%22%7D" className="bg-white rounded-xl border border-border p-6">
        <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:158:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="158" data-component-end-line="165" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:159:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="159" data-component-end-line="159" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20mb-4%22%7D" className="h-8 bg-muted rounded mb-4"></div>
          <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:160:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="160" data-component-end-line="164" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%22%7D" className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }).map((_, i) => (
              <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:162:14" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="162" data-component-end-line="162" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-square%20bg-muted%20rounded%22%7D" key={i} className="aspect-square bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:171:4" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="171" data-component-end-line="220" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-xl%20border%20border-border%20p-4%20sm%3Ap-6%22%7D" className="bg-white rounded-xl border border-border p-4 sm:p-6">
      {/* Calendar Header */}
      <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:173:6" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="173" data-component-end-line="195" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%20sm%3Amb-6%22%7D" className="flex items-center justify-between mb-4 sm:mb-6">
        <button data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:174:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="174" data-component-end-line="183" data-component-file="DateSelectionCalendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-10%20h-10%20sm%3Aw-12%20sm%3Ah-12%20rounded-lg%20hover%3Abg-muted%20transition-colors%20touch-manipulation%22%7D"
          onClick={prevMonth}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg hover:bg-muted transition-colors touch-manipulation"
          disabled={
            currentMonth.getMonth() === new Date().getMonth() &&
            currentMonth.getFullYear() === new Date().getFullYear()
          }
        >
          <Icon data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:182:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="182" data-component-end-line="182" data-component-file="DateSelectionCalendar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%2C%22className%22%3A%22text-foreground%22%7D" name="ChevronLeftIcon" size={20} className="text-foreground" />
        </button>

        <h3 data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:185:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="185" data-component-end-line="187" data-component-file="DateSelectionCalendar.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-base%20sm%3Atext-lg%20font-semibold%20text-foreground%22%7D" className="text-base sm:text-lg font-semibold text-foreground">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:189:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="189" data-component-end-line="194" data-component-file="DateSelectionCalendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-10%20h-10%20sm%3Aw-12%20sm%3Ah-12%20rounded-lg%20hover%3Abg-muted%20transition-colors%20touch-manipulation%22%7D"
          onClick={nextMonth}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg hover:bg-muted transition-colors touch-manipulation"
        >
          <Icon data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:193:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="193" data-component-end-line="193" data-component-file="DateSelectionCalendar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22text-foreground%22%7D" name="ChevronRightIcon" size={20} className="text-foreground" />
        </button>
      </div>

      {/* Day Labels */}
      <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:198:6" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="198" data-component-end-line="204" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-1%20sm%3Agap-2%20mb-2%22%7D" className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:200:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="200" data-component-end-line="202" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20text-%5B10px%5D%20sm%3Atext-xs%20font-medium%20text-muted-foreground%22%7D" key={day} className="text-center text-[10px] sm:text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:207:6" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="207" data-component-end-line="207" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-1%20sm%3Agap-2%22%7D" className="grid grid-cols-7 gap-1 sm:gap-2">{renderCalendar()}</div>

      {/* Legend */}
      <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:210:6" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="210" data-component-end-line="219" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%20sm%3Amt-6%20pt-4%20sm%3Apt-6%20border-t%20border-border%20flex%20flex-wrap%20items-center%20gap-3%20sm%3Agap-4%20text-xs%20text-muted-foreground%22%7D" className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-muted-foreground">
        <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:211:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="211" data-component-end-line="214" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
          <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:212:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="212" data-component-end-line="212" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-4%20h-4%20bg-primary%20rounded%22%7D" className="w-4 h-4 bg-primary rounded"></div>
          <span data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:213:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="213" data-component-end-line="213" data-component-file="DateSelectionCalendar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Selected%22%7D">Selected</span>
        </div>
        <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:215:8" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="215" data-component-end-line="218" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
          <div data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:216:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="216" data-component-end-line="216" data-component-file="DateSelectionCalendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-4%20h-4%20bg-muted%20rounded%22%7D" className="w-4 h-4 bg-muted rounded"></div>
          <span data-component-id="src/app/booking/components/DateSelectionCalendar.tsx:217:10" data-component-path="src/app/booking/components/DateSelectionCalendar.tsx" data-component-line="217" data-component-end-line="217" data-component-file="DateSelectionCalendar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Unavailable%22%7D">Unavailable</span>
        </div>
      </div>
    </div>
  );
}